import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { SiteFooter } from "../../components/footer/SiteFooter";
import { MarketingHeader } from "../marketing/MarketingHeader";
import {
  getMembershipCurrentApi,
  getMembershipPlansApi,
  createMembershipOrderApi,
  getMembershipOrderStatusApi,
  triggerMembershipRefresh,
  type MembershipCurrentRespVO,
  type MemberPlanRespVO,
  type MemberOrderRespVO,
  type BillingCycle,
} from "../../service/membership";
import { globalStore } from "../../utils/globalStore";
import { globalAlert } from "../../utils/globalAlert";
import { LEGAL_DOC_HREFS } from "../../legal/legalDocRoutes";
import "./PricingPage.scss";

/* --------------------------------------------------------------------------
 * 装饰条（纯 UI 动效，与数据无关）
 * -------------------------------------------------------------------------- */
const WAVE_HEIGHTS = [
  4, 5, 7, 6, 8, 10, 7, 6, 9, 12, 18, 11, 7, 6, 8, 14, 20, 12, 8, 6, 7, 10, 16, 12, 8, 6, 5, 7, 11, 15, 10, 7, 5, 4,
];
const SIGNAL_HEIGHTS = [
  5, 6, 7, 9, 12, 17, 25, 37, 54, 78, 104, 82, 61, 45, 33, 25, 19, 15, 12, 10, 8, 7, 6, 5,
];

type Cycle = "month" | "year";
type Method = "wechat" | "alipay";

/** 把后端 billingCycle 标准化为内部 cycle 或 'free' */
const normalizeCycle = (bc: BillingCycle | undefined): Cycle | "free" => {
  const s = String(bc ?? "").trim().toLowerCase();
  if (!s || s === "free") return "free";
  if (s === "year" || /annual|y/.test(s)) return "year";
  return "month";
};

/** 把 cycle / free 映射成中文标签 */
const formatCycleLabel = (value: Cycle | "free"): string => {
  if (value === "free") return "免费";
  if (value === "year") return "年付";
  return "月付";
};

/** 把 ISO 时间裁剪为 YYYY.MM.DD */
const formatDate = (iso: string | undefined): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}.${m}.${day}`;
};

const numberFmt = new Intl.NumberFormat("zh-CN");
/** 金额格式化：固定 2 位小数（避免 0.01 被 Math.round 抹成 0） */
const currencyFmt = new Intl.NumberFormat("zh-CN", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** 归类后用于渲染的三张卡片 */
interface PlanCard {
  /** 卡片层级（用于排序/渲染）：'free' | 'basic' | 'pro' | 'enterprise' */
  tier: string;
  /** 卡片展示名：优先 plan.name；无数据时占位 */
  name: string;
  description: string;
  /** 该 tier 下 cycle=month 对应的套餐记录（用于渲染月价/积分） */
  monthRecord: MemberPlanRespVO | undefined;
  /** 该 tier 下 cycle=year 对应的套餐记录（用于计算年付金额与节省） */
  yearRecord: MemberPlanRespVO | undefined;
}

/* --------------------------------------------------------------------------
 * Page
 * -------------------------------------------------------------------------- */
export const PricingPage: FC = () => {
  const [cycle, setCycle] = useState<Cycle>("month");
  const [payment, setPayment] = useState<{
    open: boolean;
    order: MemberOrderRespVO | null;
    method: Method;
    /** 轮询目标 orderNo */
    pollOrderNo: string | null;
    /** 当前轮询轮次 > 0 表示在轮询中 */
    pollTick: number;
    /** 弹窗内下单所用 planId；切换支付方式时用于复用同一 planId 重新下单 */
    planId: number | null;
    /** 二维码加载态：true 表示正在请求新订单 / 切换支付方式 */
    qrLoading: boolean;
  }>({
    open: false,
    order: null,
    method: "wechat",
    pollOrderNo: null,
    pollTick: 0,
    planId: null,
    qrLoading: false,
  });
  /** 文字内容；动画期间保留，动画结束后才清空（避免"空框闪一下"） */
  const [toastText, setToastText] = useState<string | null>(null);
  /** 控制 .show className；切到 false 触发淡出动画 */
  const [toastVisible, setToastVisible] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [salesModalOpen, setSalesModalOpen] = useState(false);
  const [submittingTier, setSubmittingTier] = useState<string | null>(null);
  const [qrExpired, setQrExpired] = useState(false);
  const lastTriggerRef = useRef<HTMLElement | null>(null);
  /** 异步竞态保护：每次重新下单 sid 自增，旧轮询 / 旧过期 timer 失效 */
  const paySessionRef = useRef(0);
  /** 跟踪所有活跃轮询 interval；用 Set 而非单一 ref，
   *  防止新 bootstrap 在旧 API 尚未返回时启动 → 旧 interval 句柄丢失 → 后台持续轮询旧订单 */
  const pollIntervalsRef = useRef<Set<number>>(new Set());
  const expiryTimerRef = useRef<number | null>(null);
  /** 会员二维码有效期（与积分包 60s 区分）：30 分钟 */
  const QR_EXPIRY_MS = 30 * 60 * 1000;

  /* 数据：当前会员 + 套餐列表 */
  const [current, setCurrent] = useState<MembershipCurrentRespVO | null>(null);
  const [plans, setPlans] = useState<MemberPlanRespVO[]>([]);
  const [dataLoading, setDataLoading] = useState(true);

  const showToast = useCallback((msg: string) => {
    setToastText(msg);
    setToastVisible(true);
  }, []);

  /* 0. 注入装饰条：依赖 plans，异步加载完套餐后才会渲染 .plan-wave 节点 */
  useEffect(() => {
    document
      .querySelectorAll<HTMLElement>(".pricing-page .plan-wave")
      .forEach((wave, wi) => {
        wave.innerHTML = WAVE_HEIGHTS.map((_, i) => {
          const h = WAVE_HEIGHTS[(i + wi * 4) % WAVE_HEIGHTS.length];
          return `<i style="--h:${h}px"></i>`;
        }).join("");
      });
    document
      .querySelectorAll<HTMLElement>(".pricing-page .signal-cluster")
      .forEach((cluster) => {
        cluster.innerHTML = SIGNAL_HEIGHTS.map((h, i) => {
          const o = Math.max(0.18, 0.72 - i * 0.018).toFixed(2);
          return `<i style="--h:${h}px;--o:${o}"></i>`;
        }).join("");
      });
  }, [plans]);

  /* 1. 加载数据：当前会员 + 套餐列表 */
  const loadData = useCallback(async () => {
    setDataLoading(true);
    const [currentRes, plansRes] = await Promise.all([
      getMembershipCurrentApi().catch(() => null),
      getMembershipPlansApi().catch(() => null),
    ]);
    if (currentRes?.code === 0 && currentRes.data) {
      setCurrent(currentRes.data);
    } else {
      setCurrent(null);
    }
    if (plansRes?.code === 0 && Array.isArray(plansRes.data)) {
      const enabled = plansRes.data.filter((p) => p.status === undefined || p.status === 1);
      enabled.sort((a, b) => {
        const ao = a.sortOrder ?? 999;
        const bo = b.sortOrder ?? 999;
        if (ao !== bo) return ao - bo;
        return (a.level ?? 0) - (b.level ?? 0);
      });
      setPlans(enabled);
    } else {
      setPlans([]);
    }
    setDataLoading(false);
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  /* 2. 把 plans 归类为三张卡片（按 level 分组：level 0 = free / level 1 = basic / level 2 = pro）
   *    每组内分别取 monthRecord / yearRecord；free 仅一条记录 */
  const planCards = useMemo<PlanCard[]>(() => {
    const free = plans.find((p) => p.freePlan === true);

    const nonFree = plans.filter((p) => p.freePlan !== true);
    const byLevel = new Map<number, MemberPlanRespVO[]>();
    for (const p of nonFree) {
      const lvl = p.level ?? 0;
      const arr = byLevel.get(lvl) ?? [];
      arr.push(p);
      byLevel.set(lvl, arr);
    }
    const levels = [...byLevel.keys()].sort((a, b) => a - b);

    const buildGroup = (
      group: MemberPlanRespVO[] | undefined
    ): { monthRecord: MemberPlanRespVO | undefined; yearRecord: MemberPlanRespVO | undefined } => {
      const monthRecord = group?.find((p) => normalizeCycle(p.billingCycle) === "month");
      const yearRecord = group?.find((p) => normalizeCycle(p.billingCycle) === "year");
      // 免费套餐通常只有一条记录且 billingCycle 不是 "month"（如 "free"），
      // 此时 monthRecord 会是 undefined，导致 grantPoint 渲染为 0。
      // 月份记录兜底取组内首条，确保积分/价格派生不为空。
      return {
        monthRecord: monthRecord ?? group?.[0],
        yearRecord,
      };
    };

    const level1Group = byLevel.get(levels[0] ?? -1);
    const level2Group = byLevel.get(levels[1] ?? -1);

    const level1Anchor =
      level1Group?.find((p) => normalizeCycle(p.billingCycle) === "month") ?? level1Group?.[0];
    const level2Anchor =
      level2Group?.find((p) => normalizeCycle(p.billingCycle) === "month") ?? level2Group?.[0];

    return [
      {
        tier: "free",
        name: free?.name ?? "免费版",
        description: "适合体验产品与验证一个声音创意。",
        ...buildGroup(free ? [free] : undefined),
      },
      {
        tier: "basic",
        name: level1Anchor?.name ?? "基础会员",
        description: "适合稳定更新内容的个人创作者。",
        ...buildGroup(level1Group),
      },
      {
        tier: "pro",
        name: level2Anchor?.name ?? "高级会员",
        description: "适合高频、专业且多场景的声音创作。",
        ...buildGroup(level2Group),
      },
    ];
  }, [plans]);

  /** 卡片名跟随当前 cycle：API 返回 name 通常带「（月付）/（年付）」后缀
   *    - free：无 cycle 概念，直接用其自身名称
   *    - basic/pro：取当前 cycle 对应记录的 name */
  const getDisplayName = useCallback(
    (card: PlanCard): string => {
      if (card.tier === "free") return card.monthRecord?.name ?? "免费版";
      const rec = cycle === "year" ? card.yearRecord : card.monthRecord;
      return rec?.name ?? card.name;
    },
    [cycle]
  );

  /* 3. 当前方案摘要 */
  const currentSummary = useMemo(() => {
    if (!current || !current.membershipId || current.membershipId <= 0) {
      return null;
    }
    const plan = plans.find((p) => p.id === current.planId);
    const planName = plan?.name ?? current.planName ?? "当前会员";
    const cycleLabel = formatCycleLabel(normalizeCycle(current.billingCycle));
    const endAt = formatDate(current.endTime);
    const grantPoint = plan?.grantPoint ?? 0;
    return {
      planName,
      cycleLabel,
      endAt,
      grantPoint: numberFmt.format(grantPoint),
    };
  }, [current, plans]);

  /** 用户当前订阅在 5 档套餐中的 rank：
   *    free(0) < 基础月付(1) < 高级月付(2) < 基础年付(3) < 高级年付(4)
   *    用「套餐 rank + cycle rank」比较，跨月/年维度也能正确判定高低档
   *    （如 pro月付 用户看 basic年付 仍可点击购买）。未订阅返回 null。 */
  const userCurrentRank = useMemo<number | null>(() => {
    if (!current?.membershipId || current.membershipId <= 0) return null;
    const plan = plans.find((p) => p.id === current.planId);
    if (plan?.freePlan) return 0;
    const base = plan?.level === 2 ? 2 : 1;
    const cyc = normalizeCycle(current.billingCycle);
    return cyc === "year" ? base + 2 : base;
  }, [current, plans]);

  /** 用户是否已登录（globalStore.userInfo.id > 0）；未登录时不显示摘要卡 */
  const isLoggedIn = useMemo<boolean>(() => {
    const u = globalStore.getUserInfo() as { id?: number } | null | undefined;
    return typeof u?.id === "number" && u.id > 0;
  }, []);

  /** 卡片在 5 档套餐中的 rank（free=0；basic/pro 月付=1/2，年付=3/4） */
  const getCardRank = useCallback(
    (card: PlanCard): number => {
      if (card.tier === "free") return 0;
      const base = card.tier === "pro" ? 2 : 1;
      return cycle === "year" ? base + 2 : base;
    },
    [cycle]
  );

  /* 4. 价格派生：永远显示「X 元/月」
   *    - free (level 0)：始终 ¥0/月（API 单条记录且必为 0）
   *    - basic/pro：cycle=month 取 monthRecord.priceYuan；cycle=year 取 yearRecord.priceYuan / 12
   */
  const getMonthlyPrice = useCallback(
    (card: PlanCard): number => {
      if (card.tier === "free") return 0;
      const v = Number(
        (cycle === "year" ? card.yearRecord : card.monthRecord)?.priceYuan ?? 0
      );
      if (cycle === "year") return Math.round(v / 12);
      return v;
    },
    [cycle]
  );

  /** 省钱文案：free 无年付对应记录，始终不展示；
   *    - cycle=month：「年付 ¥X，可省 ¥Y」（数据来自年付记录）
   *    - cycle=year ：「年付 ¥X/年 · 已省 ¥Y」
   *    saving = monthRecord.priceYuan * 12 - yearRecord.priceYuan
   */
  const getSavingText = useCallback(
    (card: PlanCard): string | null => {
      if (card.tier === "free") return null;
      const month = card.monthRecord;
      const year = card.yearRecord;
      if (!month || !year) return null;
      const totalMonth = Number(month.priceYuan ?? 0) * 12;
      const totalYear = Number(year.priceYuan ?? 0);
      const saving = totalMonth - totalYear;
      if (saving <= 0 || totalYear <= 0) return null;
      if (cycle === "year") {
        return `年付 ¥${numberFmt.format(totalYear)}/年 · 已省 ¥${numberFmt.format(saving)}`;
      }
      return `年付 ¥${numberFmt.format(totalYear)}，可省 ¥${numberFmt.format(saving)}`;
    },
    [cycle]
  );

  /* 5. 轮询已迁移到 bootstrapPay 的 runPollOnce + pollIntervalsRef（set 跟踪），不再使用 useEffect */

  /* Esc 关闭 */
  useEffect(() => {
    if (!payment.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closePayment();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment.open]);

  /* toast：双定时器控制"先淡出、再卸载内容"
   *  - 2400ms：toastVisible=false 触发淡出动画（.32s + .38s ~700ms）
   *  - 2400ms + 450ms：toastText=null 卸载 DOM（此时 opacity 已为 0，视觉上看不到内容消失）
   */
  useEffect(() => {
    if (!toastVisible) return;
    const fadeTimer = window.setTimeout(() => setToastVisible(false), 2400);
    const removeTimer = window.setTimeout(() => setToastText(null), 2400 + 450);
    return () => {
      window.clearTimeout(fadeTimer);
      window.clearTimeout(removeTimer);
    };
  }, [toastVisible]);

  /* 弹窗打开锁滚 */
  useEffect(() => {
    document.body.classList.toggle("payment-open", payment.open);
    return () => document.body.classList.remove("payment-open");
  }, [payment.open]);

  /* 清除支付错误：弹窗关闭或 method 切换时 */
  useEffect(() => {
    if (!payment.open) setPaymentError(null);
  }, [payment.open]);

  /* ----------------------------------------------------------------- */
  /* handlers                                                            */
  /* ----------------------------------------------------------------- */

  /** 仅停止轮询（保留过期 timer） */
  const stopPollingOnly = useCallback(() => {
    pollIntervalsRef.current.forEach((id) => window.clearInterval(id));
    pollIntervalsRef.current.clear();
  }, []);

  /** 清理轮询 + 过期 timer */
  const clearPayTimers = useCallback(() => {
    stopPollingOnly();
    if (expiryTimerRef.current != null) {
      window.clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
  }, [stopPollingOnly]);

  const closePayment = useCallback(() => {
    clearPayTimers();
    paySessionRef.current += 1;
    setPayment((p) => ({
      ...p,
      open: false,
      order: null,
      pollOrderNo: null,
      planId: null,
      qrLoading: false,
    }));
    setQrExpired(false);
    lastTriggerRef.current?.focus?.();
  }, [clearPayTimers]);

  /** 单次轮询：受 sid 防旧回调；命中终态时清 timer 并触发对应动作 */
  const runPollOnce = useCallback(
    async (orderNo: string, sid: number) => {
      if (sid !== paySessionRef.current) return;
      try {
        const res = await getMembershipOrderStatusApi(orderNo);
        if (sid !== paySessionRef.current) return;
        const order = res?.data;
        if (!order) return;
        if (order.paymentStatus === "success" || order.paymentStatus === "paid") {
          clearPayTimers();
          globalAlert.showSuccess("会员开通成功");
          triggerMembershipRefresh();
          void loadData();
          closePayment();
        } else if (
          order.paymentStatus === "failed" ||
          order.paymentStatus === "closed"
        ) {
          stopPollingOnly();
          setPaymentError("支付未成功，请重试或更换支付方式。");
        }
      } catch {
        /* 单次失败忽略，下次轮询继续 */
      }
    },
    [clearPayTimers, stopPollingOnly, showToast, loadData, closePayment]
  );

  /** 统一下单：清旧 timer / sid++ / 创建订单 / 启动轮询 + 30 分钟过期
   *    注意：不清空 `order`，仅更新 qrCodeImage 等字段，避免左侧文字闪到「—」/「0」 */
  const bootstrapPay = useCallback(
    async (planId: number, method: Method) => {
      clearPayTimers();
      paySessionRef.current += 1;
      const sid = paySessionRef.current;
      setQrExpired(false);
      setPaymentError(null);
      setPayment((p) => ({
        ...p,
        method,
        qrLoading: true,
      }));

      try {
        const res = await createMembershipOrderApi({
          planId,
          payChannel: method === "wechat" ? "wechat_native" : "alipay_qrcode",
        });
        if (sid !== paySessionRef.current) return;
        if (res?.code !== 0 || !res.data) {
          setPaymentError(res?.msg || "订单创建失败，请稍后重试");
          setPayment((p) => ({ ...p, qrLoading: false }));
          return;
        }
        const next = res.data;
        const orderNo = next.orderNo;
        if (!orderNo) {
          setPaymentError("订单缺少流水号，请重试");
          setPayment((p) => ({ ...p, qrLoading: false }));
          return;
        }
        setPayment((p) => ({
          ...p,
          method,
          order: { ...(p.order ?? {}), ...next },
          pollOrderNo: orderNo,
          qrLoading: false,
        }));

        void runPollOnce(orderNo, sid);
        pollIntervalsRef.current.add(
          window.setInterval(() => {
            void runPollOnce(orderNo, sid);
          }, 2000)
        );
        expiryTimerRef.current = window.setTimeout(() => {
          if (sid !== paySessionRef.current) return;
          setQrExpired(true);
          stopPollingOnly();
        }, QR_EXPIRY_MS);
      } catch {
        if (sid === paySessionRef.current) {
          setPaymentError("订单创建失败，请稍后重试");
          setPayment((p) => ({ ...p, qrLoading: false }));
        }
      }
    },
    [clearPayTimers, stopPollingOnly, runPollOnce]
  );

  /** 切换支付方式：复用 planId 重新下单；bootstrapPay 内部 sid++ 让旧 timer/轮询失效 */
  const setMethod = useCallback(
    (method: Method) => {
      if (!payment.open || payment.planId == null) {
        setPayment((p) => ({ ...p, method }));
        return;
      }
      if (payment.method === method) return;
      void bootstrapPay(payment.planId, method);
    },
    [payment.open, payment.planId, payment.method, bootstrapPay]
  );

  /** 蒙层「刷新二维码」按钮：用当前 planId + method 重新下单（重启 30 分钟计时） */
  const handleRefreshQr = useCallback(() => {
    if (payment.planId == null) return;
    void bootstrapPay(payment.planId, payment.method);
  }, [payment.planId, payment.method, bootstrapPay]);

  /** 检查登录状态（未登录时跳转登录） */
  const requireLogin = useCallback((): boolean => {
    const u = globalStore.getUserInfo() as { id?: number } | null | undefined;
    if (!u?.id) {
      showToast("请先登录后再开通会员");
      return false;
    }
    return true;
  }, [showToast]);

/** 创建订单并打开支付弹窗 */
  const startUpgrade = useCallback(
    (card: PlanCard, trigger: HTMLElement) => {
      if (!requireLogin()) return;
      const record = cycle === "year" ? card.yearRecord : card.monthRecord;
      if (!record?.id) {
        showToast("该套餐暂支持当前周期");
        return;
      }
      lastTriggerRef.current = trigger;
      setSubmittingTier(card.tier);
      const planId = Number(record.id);
      // 用卡片自身数据预填左侧订单信息，避免接口返回前闪到「—」/「0」
      const seedOrder: MemberOrderRespVO = {
        planId,
        planCode: record.code,
        planName: record.name,
        billingCycle: record.billingCycle,
        grantPoint: record.grantPoint,
        amountYuan: record.priceYuan,
      };
      setPayment({
        open: true,
        order: seedOrder,
        method: "wechat",
        pollOrderNo: null,
        pollTick: Date.now(),
        planId,
        qrLoading: true,
      });
      void bootstrapPay(planId, "wechat").finally(() => {
        setSubmittingTier(null);
      });
    },
    [cycle, requireLogin, showToast, bootstrapPay]
  );

  /** 手动轮询一次（"我已完成支付" 按钮兜底） */
  const pollOnce = useCallback(() => {
    if (!payment.pollOrderNo) return;
    void runPollOnce(payment.pollOrderNo, paySessionRef.current);
  }, [payment.pollOrderNo, runPollOnce]);

  /* ----------------------------------------------------------------- */
  /* 渲染辅助                                                            */
  /* ----------------------------------------------------------------- */

  /** 判断某张卡片是否为用户当前方案 */
  const isCurrentCard = useCallback(
    (card: PlanCard): boolean => {
      if (!current?.membershipId || current.membershipId <= 0) return false;
      const target = cycle === "year" ? card.yearRecord : card.monthRecord;
      return target?.id != null && Number(target.id) === Number(current.planId);
    },
    [current, cycle]
  );

  const renderPlanArticle = (card: PlanCard, idx: number) => {
    const isFree = card.tier === "free";
    const isPro = card.tier === "pro";
    const currentFlag = isCurrentCard(card);
    /** 用户当前订阅的 5 档 rank 高于本卡 rank 时：按钮降级为「无需开通」disabled
     *    例：pro月付用户(2) 看 basic年付(3) → rank 2 < 3 → 仍可点击升级（年付价更便宜） */
    const cardRank = getCardRank(card);
    const isHigherTier =
      !currentFlag &&
      userCurrentRank != null &&
      cardRank > 0 &&
      cardRank < userCurrentRank;
    const monthlyPrice = getMonthlyPrice(card);
    const savingText = getSavingText(card);
    /** 当前选中 cycle 对应的记录；free 始终用自己的 monthRecord（无年付对应记录） */
    const activeRecord = isFree
      ? card.monthRecord
      : cycle === "year"
      ? card.yearRecord
      : card.monthRecord;

    const cardClass = [
      "plan",
      isFree ? "free" : card.tier,
      currentFlag ? "current" : "",
      isPro ? "featured" : "",
    ]
      .filter(Boolean)
      .join(" ");

    const grantPoint = activeRecord?.grantPoint ?? 0;

    return (
      <article key={card.tier} className={cardClass} data-tier={card.tier}>
        <div className="plan-top">
          <div className="plan-name">
            <i className="plan-dot" aria-hidden="true" />
            <h3>{getDisplayName(card)}</h3>
          </div>
          {currentFlag ? (
            <span className="current-label">当前方案</span>
          ) : isPro ? (
            <span className="popular">最受欢迎</span>
          ) : null}
        </div>
        <p className="plan-for">{card.description}</p>
        <div className="plan-wave" aria-hidden="true" />
        <div className="price-wrap">
          <p className="price">
            ¥{numberFmt.format(monthlyPrice)} <small>/ 月</small>
          </p>
        </div>
        <p className={`annual-note ${savingText ? "" : "empty"}`}>
          {savingText ?? "占位"}
        </p>
        <div className="credit-box">
          <div>
            <span>含创作积分</span>
            <strong>{numberFmt.format(grantPoint)}</strong>
          </div>
          <em>POINTS</em>
        </div>

        {currentFlag ? (
          <a className="btn current" href="#" aria-disabled="true">
            当前方案
          </a>
        ) : isFree ? (
          (() => {
            const uid = globalStore.getUserInfo() as { id?: number } | null | undefined;
            return uid?.id ? (
              <a className="btn current" href="#" aria-disabled="true">
                无需开通
              </a>
            ) : (
              <Link className="btn" to="/login">
                免费开始 <span className="arrow">→</span>
              </Link>
            );
          })()
        ) : isHigherTier ? (
          <a className="btn current" href="#" aria-disabled="true">
            无需开通
          </a>
        ) : (
          <button
            className="btn blue"
            type="button"
            disabled={submittingTier === card.tier || !activeRecord?.id}
            onClick={(e) => startUpgrade(card, e.currentTarget)}
          >
            {submittingTier === card.tier ? (
              <Loader2
                size={16}
                strokeWidth={2.4}
                className="btn-spinner"
                aria-label="正在下单"
              />
            ) : (
              <>
                升级至{getDisplayName(card)} <span className="arrow">→</span>
              </>
            )}
          </button>
        )}
      </article>
    );
  };

  return (
    <div className="pricing-page">
      <MarketingHeader />

      <main>
        <section className="plans-section" aria-labelledby="page-title">
          <div className="pricing-signal" aria-hidden="true">
            <div className="signal-cluster signal-left" />
            <div className="signal-spine" />
            <div className="signal-cluster signal-right" />
          </div>

          <div className="wrap">
            {/* 当前方案摘要：未登录直接隐藏整块（不留空框） */}
            {isLoggedIn ? (
              <div className="current-plan-summary">
                {currentSummary ? (
                  <div>
                    <strong>
                      当前方案：{currentSummary.planName} · {currentSummary.cycleLabel}
                    </strong>
                    {"　"}
                    <span>
                      有效期至 {currentSummary.endAt}，包含{" "}
                      {currentSummary.grantPoint} 创作积分
                    </span>
                  </div>
                ) : (
                  <div>
                    <strong>尚未订阅会员</strong>
                    {"　"}
                    <span>选择一个方案开启稳定创作</span>
                  </div>
                )}
              </div>
            ) : null}

            <div className="section-head">
              <div>
                <span className="section-kicker">PRICING · MEMBERSHIP</span>
                <h1 id="page-title">选择适合你的创作节奏</h1>
              </div>
              <div className="section-actions">
                <div className="billing-row">
                  <div className="billing-toggle" role="group" aria-label="选择计费周期">
                    <button
                      type="button"
                      className={cycle === "month" ? "active" : ""}
                      onClick={() => setCycle("month")}
                      aria-pressed={cycle === "month"}
                    >
                      月付
                    </button>
                    <button
                      type="button"
                      className={cycle === "year" ? "active" : ""}
                      onClick={() => setCycle("year")}
                      aria-pressed={cycle === "year"}
                    >
                      年付
                    </button>
                  </div>
                  <span className="saving-badge">年付省 2 个月</span>
                </div>
              </div>
            </div>

            <div className="plans" id="membership-plans" aria-label="会员订阅方案">
              {dataLoading && plans.length === 0 ? (
                <div className="plans-loading">正在加载套餐…</div>
              ) : (
                planCards.map((c, i) => renderPlanArticle(c, i))
              )}
            </div>

            <article className="enterprise">
              <div className="enterprise-copy">
                <span className="enterprise-kicker">LYCHEEVOICE FOR BUSINESS</span>
                <h3>企业定制方案</h3>
                <p>为团队提供可控、稳定、可扩展的语音基础设施。</p>
              </div>
              <div className="enterprise-features" aria-label="企业方案能力">
                <span>团队共享积分</span>
                <span>定制并发额度</span>
                <span>私有化与 SLA</span>
                <span>专属技术支持</span>
              </div>
              <button
                type="button"
                className="btn"
                onClick={() => setSalesModalOpen(true)}
              >
                联系销售 <span className="arrow">→</span>
              </button>
            </article>

            <div className="plan-notes">
              <span>会员积分当期有效，到期清零 · 积分包购买后永久有效</span>
            </div>
          </div>
        </section>
      </main>

      {/* 支付弹窗 */}
      <div
        className="payment-overlay"
        hidden={!payment.open}
        onClick={(e) => {
          if (e.target === e.currentTarget) closePayment();
        }}
      >
        <section
          className="payment-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="payment-title"
        >
          <header className="payment-head">
            <div>
              <span className="payment-kicker">SECURE CHECKOUT</span>
              <h2 id="payment-title">确认支付</h2>
            </div>
            <button
              className="payment-close"
              type="button"
              aria-label="关闭支付弹窗"
              onClick={closePayment}
            >
              ×
            </button>
          </header>
          <div className="payment-body">
            <div className="payment-summary">
              <span className="payment-order-type">会员升级</span>
              <h3>{payment.order?.planName ?? "—"}</h3>
              <p className="payment-order-description">
                {(payment.order?.planName ?? "").includes("基础会员")
                  ? "适合稳定更新的声音创作，开通后每月包含"
                  : "适合高频、专业且多场景的声音创作，开通后每月包含"}{" "}
                {numberFmt.format(payment.order?.grantPoint ?? 0)} 创作积分。
              </p>
              <div className="payment-order-lines">
                <div className="payment-order-line">
                  <span>计费周期</span>
                  <strong>
                    {payment.order?.billingCycle
                      ? `${formatCycleLabel(normalizeCycle(payment.order.billingCycle))} · 1 ${normalizeCycle(payment.order.billingCycle) === "year" ? "年" : "月"}`
                      : "—"}
                  </strong>
                </div>
                <div className="payment-order-line">
                  <span>含创作积分</span>
                  <strong>
                    {numberFmt.format(payment.order?.grantPoint ?? 0)} 积分
                  </strong>
                </div>
                {/* 续费规则已暂时隐藏
                <div className="payment-order-line is-bonus">
                  <span>续费规则</span>
                  <strong>到期自动续费</strong>
                </div>
                */}
              </div>
              <div className="payment-total">
                <span>应付金额</span>
                <strong>
                  ¥
                  {currencyFmt.format(Number(payment.order?.amountYuan ?? 0))}
                </strong>
              </div>
            </div>

            <div className="payment-panel">
              <span className="payment-panel-label">选择支付方式</span>
              <div className="payment-methods" role="group" aria-label="支付方式">
                <button
                  type="button"
                  className={`payment-method${payment.method === "wechat" ? " active" : ""}`}
                  aria-pressed={payment.method === "wechat"}
                  onClick={() => void setMethod("wechat")}
                >
                  <i>微</i>微信支付
                </button>
                <button
                  type="button"
                  className={`payment-method${payment.method === "alipay" ? " active" : ""}`}
                  aria-pressed={payment.method === "alipay"}
                  onClick={() => void setMethod("alipay")}
                >
                  <i>支</i>支付宝
                </button>
              </div>
              <div className="payment-qr-wrap">
                <div
                  className={`payment-qr${qrExpired && payment.order?.qrCodeImage ? " is-expired" : ""}`}
                  aria-label="支付二维码"
                >
                  {payment.order?.qrCodeImage ? (
                    <img
                      className="payment-qr-image"
                      alt={`${payment.method === "wechat" ? "微信" : "支付宝"}支付二维码`}
                      src={
                        payment.order.qrCodeImage.startsWith("data:")
                          ? payment.order.qrCodeImage
                          : `data:image/png;base64,${payment.order.qrCodeImage}`
                      }
                    />
                  ) : payment.qrLoading ? (
                    <div className="payment-qr-loading">
                      <span className="payment-qr-spinner" aria-hidden="true" />
                      <span className="payment-qr-loading-hint">正在获取支付二维码…</span>
                    </div>
                  ) : (
                    <span className="payment-qr-logo">
                      {payment.method === "wechat" ? "微" : "支"}
                    </span>
                  )}
                  {qrExpired && payment.order?.qrCodeImage ? (
                    <button
                      type="button"
                      className="payment-qr-expired-mask"
                      onClick={handleRefreshQr}
                    >
                      <span className="payment-qr-expired-icon" aria-hidden="true">
                        ↻
                      </span>
                      <span>二维码已过期，点击刷新</span>
                    </button>
                  ) : null}
                </div>
              </div>
              <p className="payment-scan-title">
                {payment.method === "wechat" ? "请使用微信扫码支付" : "请使用支付宝扫码支付"}
              </p>
              {paymentError ? (
                <p className="payment-error" role="alert">
                  {paymentError}
                </p>
              ) : null}
              <p className="payment-terms">
                支付即代表您已阅读并同意
                <br />
                <a
                  href={LEGAL_DOC_HREFS.userAgreement}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  《用户协议》
                </a>
                <a
                  href={LEGAL_DOC_HREFS.privacyPolicy}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  《隐私政策》
                </a>
                <a
                  href={LEGAL_DOC_HREFS.subscriptionAgreement}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  《订购服务协议》
                </a>
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* 联系销售弹窗 */}
      <div
        className="sales-overlay"
        hidden={!salesModalOpen}
        onClick={(e) => {
          if (e.target === e.currentTarget) setSalesModalOpen(false);
        }}
      >
        <section
          className="sales-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sales-modal-title"
        >
          <header className="sales-head">
            <h2 id="sales-modal-title">联系企业销售</h2>
            <button
              className="sales-close"
              type="button"
              aria-label="关闭"
              onClick={() => setSalesModalOpen(false)}
            >
              ×
            </button>
          </header>
          <div className="sales-body">
            <p className="sales-hint">扫码添加企业销售微信，获取专属方案与报价</p>
            <div className="sales-qr-wrap">
              <img
                className="sales-qr-image"
                src="/pricing/xiaoshou.jpg"
                alt="企业销售二维码"
              />
            </div>
          </div>
        </section>
      </div>

      {/* toast —— 双 state：toastText 控制内容/卸载，toastVisible 控制淡出动画 */}
      {toastText && (
        <div
          className={`toast${toastVisible ? " show" : ""}`}
          role="status"
          aria-live="polite"
        >
          {toastText}
        </div>
      )}

      <SiteFooter />
    </div>
  );
};

export default PricingPage;